import { FC, useState } from 'react'
import { Poi, Tag as TagType } from '@/js/types'
import { CirclePlus } from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tag } from '@/js/editor/components/general/Tag.tsx'
import { useTags } from '@/js/editor/hooks/api/useTags.ts'
import { useAtomValue, useSetAtom } from 'jotai'
import { tagsAtom } from '@/js/editor/state/mapAtoms.ts'
import { updatePoiDraftAtom } from '@/js/editor/state/poiAtoms.ts'

type AddTagProps = {
    poi: Poi
}

export const AddTag: FC<AddTagProps> = ({ poi }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [value, setValue] = useState('')

    const tags = useAtomValue(tagsAtom)
    const updateDraft = useSetAtom(updatePoiDraftAtom)
    const { create } = useTags()

    const addTagToDraft = (tag: TagType) => {
        if (poi.tags.some((existingTag) => String(existingTag.id) === String(tag.id))) {
            return
        }

        updateDraft({
            tags: [...poi.tags, tag],
        })
        setIsOpen(false)
        setValue('')
    }

    const createTag = async () => {
        const name = value.trim()

        if (!name) {
            return
        }

        try {
            const newTag = await create({ name })

            if (typeof newTag !== 'number') {
                addTagToDraft(newTag)
            }
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Kategorie hinzufügen"
                    className="flex items-center justify-center rounded-md p-1 hover:bg-mm-bg2"
                >
                    <CirclePlus size={18} className="cursor-pointer text-mm-gray hover:text-mm-gray2" />
                </button>
            </PopoverTrigger>

            <PopoverContent className={'z-2005 p-0 w-50'}>
                <Command>
                    <CommandInput placeholder={'Suchen oder erstellen'} value={value} onValueChange={setValue} />
                    <CommandList>
                        <CommandEmpty>Noch keine Kategorien vorhanden</CommandEmpty>
                        <CommandGroup>
                            {tags?.map((tag) => {
                                if (poi.tags.some((existingTag) => String(existingTag.id) === String(tag.id))) {
                                    return null
                                }

                                return (
                                    <CommandItem key={tag.id} value={tag.name} onSelect={() => addTagToDraft(tag)}>
                                        <Tag title={tag.name} />
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                        {value.trim() && (
                            <CommandGroup heading={'Neue Kategorie'}>
                                <CommandItem
                                    value={value}
                                    onSelect={() => {
                                        void createTag()
                                    }}
                                >
                                    <Tag title={value} icon={'plus'} />
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
